import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Package, MessageCircle, ArrowRight, Copy, Truck, CreditCard, Phone, Download } from "lucide-react";
import { toast } from "sonner";
import usePageTitle from "@/hooks/use-page-title";
import { shopClient } from "@/context/CartContext";
import { fmtDate, fmtDateTime } from "@/lib/date";

const STATUS_LABELS = {
  payment_pending: "Payment Pending",
  paid: "Paid",
  processing: "Processing",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
  refunded: "Refunded",
};

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.getElementById("rzp-checkout-js")) return resolve(true);
    const s = document.createElement("script");
    s.id = "rzp-checkout-js";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function OrderConfirmation() {
  usePageTitle("Order Confirmation");
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [rzpConfig, setRzpConfig] = useState({ configured: false });
  const [payLoading, setPayLoading] = useState(false);

  useEffect(() => {
    shopClient.get(`/shop/orders/${id}`).then((r) => setOrder(r.data)).catch((e) => {
      setErr(e?.response?.data?.detail || "Could not load order");
    });
    shopClient.get(`/payments/razorpay/config`).then((r) => setRzpConfig(r.data)).catch(() => {});
  }, [id]);

  const payWithRazorpay = async () => {
    if (!rzpConfig.configured) {
      toast.error("Payment gateway not configured yet — please contact us on WhatsApp");
      return;
    }
    setPayLoading(true);
    const ok = await loadRazorpayScript();
    if (!ok) {
      setPayLoading(false);
      toast.error("Could not load Razorpay. Check your internet.");
      return;
    }
    try {
      const { data } = await shopClient.post("/payments/razorpay/create-order", { shop_order_id: order.id });
      const options = {
        key: rzpConfig.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: "Brothers Advertising",
        description: `Order ${data.shop_order_no}`,
        prefill: {
          name: data.customer.name || "",
          email: data.customer.email || "",
          contact: data.customer.contact || "",
        },
        theme: { color: "#FF6B00" },
        handler: async (response) => {
          try {
            await shopClient.post("/payments/razorpay/verify", {
              shop_order_id: order.id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment successful!");
            const { data: fresh } = await shopClient.get(`/shop/orders/${order.id}`);
            setOrder(fresh);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed");
          }
        },
        modal: {
          ondismiss: () => setPayLoading(false),
        },
      };
      // eslint-disable-next-line no-undef
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not initiate payment");
    } finally {
      setPayLoading(false);
    }
  };

  if (err) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-black">Order not found</h1>
        <p className="mt-3 text-zinc-500">{err}</p>
        <Link to="/products" className="mt-6 inline-flex items-center gap-2 text-[#FF6B00] font-bold">Continue Shopping <ArrowRight size={14} /></Link>
      </div>
    );
  }

  if (!order) return <div className="max-w-6xl mx-auto px-4 py-24 text-center text-zinc-500">Loading order…</div>;

  const copyOrderNo = () => {
    navigator.clipboard.writeText(order.order_no);
    toast.success("Order number copied");
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      {/* Success banner */}
      <div className="text-center py-8">
        <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
          <CheckCircle2 size={44} className="text-emerald-600" />
        </div>
        <div className="mt-4 overline text-emerald-600">Order Confirmed</div>
        <h1 className="mt-2 font-display text-4xl md:text-5xl font-black tracking-tighter" data-testid="confirmation-title">
          Thank you for your order!
        </h1>
        <p className="mt-3 text-zinc-600">We've received your order and will be in touch shortly.</p>
        <div className="mt-6 inline-flex items-center gap-3 border-2 border-zinc-200 rounded-full px-6 py-3">
          <span className="text-xs uppercase text-zinc-500 tracking-wider">Order No</span>
          <span className="font-mono font-black text-lg text-[#FF6B00]" data-testid="order-no">{order.order_no}</span>
          <button onClick={copyOrderNo} className="text-zinc-400 hover:text-[#FF6B00]"><Copy size={14} /></button>
        </div>
      </div>

      {/* Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-8">
        <StatusCard icon={Package} title="Status" value={STATUS_LABELS[order.status] || order.status} accent="bg-orange-50 text-[#FF6B00]" />
        <StatusCard icon={CheckCircle2} title="Payment" value={order.payment_status === "paid" ? "Paid" : "Pending"} accent="bg-emerald-50 text-emerald-700" />
        <StatusCard icon={Truck} title="Method" value={order.payment_method?.toUpperCase() || "—"} accent="bg-zinc-100 text-zinc-800" />
      </div>

      {/* Items */}
      <div className="border-2 border-zinc-200 rounded-2xl bg-white p-6 mb-4">
        <div className="font-bold text-lg mb-3">Items</div>
        <div className="space-y-2" data-testid="conf-items">
          {order.items.map((it) => (
            <div key={it.product_id} className="flex justify-between gap-2 text-sm border-b border-zinc-100 pb-2 last:border-0">
              <div>
                <div className="font-semibold">{it.name}</div>
                <div className="text-xs text-zinc-500">Qty: {it.quantity} · ₹{Math.round(it.base_price).toLocaleString("en-IN")} {it.unit}</div>
              </div>
              <div className="font-mono font-bold">₹{Math.round(it.line_total).toLocaleString("en-IN")}</div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-zinc-200 space-y-2 text-sm">
          <Row label="Subtotal" val={order.subtotal} />
          {order.coupon_discount > 0 && <Row label={`Coupon (${order.coupon_code})`} val={-order.coupon_discount} accent="text-emerald-700" />}
          {order.gift_card_applied > 0 && <Row label={`Gift Card (${order.gift_card_code})`} val={-order.gift_card_applied} accent="text-emerald-700" />}
          <Row label="Shipping" val={order.shipping} />
          <div className="border-t border-zinc-200 pt-2 mt-2 flex justify-between text-lg">
            <span className="font-bold">Total Paid</span>
            <span className="font-mono font-black text-[#FF6B00]" data-testid="conf-total">₹{Math.round(order.total).toLocaleString("en-IN")}</span>
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="border-2 border-zinc-200 rounded-2xl bg-white p-6 mb-6">
        <div className="font-bold text-lg mb-3">Delivery Address</div>
        <div className="text-sm text-zinc-700 space-y-1">
          <div className="font-semibold">{order.address.full_name}</div>
          <div>{order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}</div>
          <div>{order.address.city}, {order.address.state} — {order.address.pincode}</div>
          {order.address.landmark && <div className="text-zinc-500">Landmark: {order.address.landmark}</div>}
          <div className="pt-2">📞 {order.address.phone}{order.address.email ? ` · ✉️ ${order.address.email}` : ""}</div>
        </div>
      </div>

      {/* CTAs */}
      <div className="flex flex-wrap gap-3 justify-center">
        {order.payment_status !== "paid" && order.total > 0 && (
          <button
            onClick={payWithRazorpay}
            disabled={payLoading}
            className="inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold px-6 py-3 rounded-full hover:bg-[#e65a00] disabled:opacity-60"
            data-testid="pay-now-btn"
          >
            <CreditCard size={16} /> {payLoading ? "Opening…" : `Pay Now ₹${Math.round(order.total).toLocaleString("en-IN")}`}
          </button>
        )}
        <Link to="/products" className="inline-flex items-center gap-2 border-2 border-zinc-300 font-bold px-6 py-3 rounded-full hover:border-[#FF6B00] hover:text-[#FF6B00]" data-testid="continue-shopping">
          Continue Shopping
        </Link>
        <a href={`https://wa.me/918070970980?text=${encodeURIComponent(`Hi, I placed order ${order.order_no}. When can I expect it?`)}`}
           target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold px-6 py-3 rounded-full hover:bg-[#1ea856]" data-testid="whatsapp-order">
          <MessageCircle size={16} /> WhatsApp Us
        </a>
        {order.payment_status === "paid" && (
          <button
            type="button"
            onClick={async () => {
              try {
                const res = await shopClient.get(`/shop/orders/${order.id}/invoice.pdf`, { responseType: "blob" });
                const blob = new Blob([res.data], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `Invoice-${order.order_no || order.id}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) {
                toast.error("Could not download invoice — please try again in a moment.");
              }
            }}
            className="inline-flex items-center gap-2 bg-zinc-900 text-white font-bold px-6 py-3 rounded-full hover:bg-zinc-800"
            data-testid="download-invoice-btn"
          >
            <Download size={16} /> Download Invoice PDF
          </button>
        )}
        {order.whatsapp_deeplink && order.payment_status === "paid" && (
          <a href={order.whatsapp_deeplink} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 border-2 border-[#25D366] text-[#25D366] font-bold px-6 py-3 rounded-full hover:bg-[#25D366] hover:text-white"
             data-testid="send-receipt-whatsapp">
            <MessageCircle size={16} /> Send Receipt on WhatsApp
          </a>
        )}
        <a href="tel:02247427059" className="inline-flex items-center gap-2 border-2 border-zinc-800 text-zinc-900 font-bold px-6 py-3 rounded-full hover:bg-zinc-900 hover:text-white" data-testid="call-customer-care">
          <Phone size={16} /> Customer Care 022 4742 7059
        </a>
      </div>

      {/* Design upload widget (always available; strongly suggested if design_upload_later) */}
      <DesignUploader order={order} onUploaded={(fresh) => setOrder(fresh)} />

      {order.payment_status !== "paid" && order.total > 0 && !rzpConfig.configured && (
        <div className="mt-4 mx-auto max-w-lg text-center text-xs text-zinc-500">
          Online payment gateway is being configured. Our team will send you a payment link on WhatsApp shortly.
        </div>
      )}

      <div className="mt-8 text-center text-xs text-zinc-500">
        Save your order number to track this order later.
      </div>
    </div>
  );
}

function StatusCard({ icon: Icon, title, value, accent }) {
  return (
    <div className="p-4 rounded-2xl border-2 border-zinc-200 bg-white flex items-center gap-3">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${accent}`}><Icon size={18} /></div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{title}</div>
        <div className="font-bold text-sm">{value}</div>
      </div>
    </div>
  );
}

function Row({ label, val, accent = "" }) {
  return (
    <div className={`flex justify-between ${accent}`}>
      <span className="text-zinc-600">{label}</span>
      <span className="font-mono font-bold">{val < 0 ? "−" : ""}₹{Math.round(Math.abs(val)).toLocaleString("en-IN")}</span>
    </div>
  );
}

function DesignUploader({ order, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const files = order.design_files || [];
  const pending = order.design_upload_later;

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB per file"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await shopClient.post(`/shop/orders/${order.id}/design-upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Design uploaded — our team will review it");
      const { data } = await shopClient.get(`/shop/orders/${order.id}`);
      onUploaded(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className={`mt-8 border-2 rounded-2xl p-6 bg-white ${pending ? "border-amber-400 bg-amber-50/40" : "border-zinc-200"}`} data-testid="design-uploader">
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="font-bold text-lg">📎 Artwork / Design File</div>
          <div className="text-xs text-zinc-500 mt-1">
            {pending
              ? "You chose to upload artwork later. Upload it here anytime within 7 days."
              : "Upload your print-ready design so our team can start production."}
            <br />JPG · PNG · PDF · AI · CDR · EPS · PSD (max 10MB per file). Files auto-delete 30 days after upload.
          </div>
        </div>
        <label className="cursor-pointer inline-flex items-center gap-2 bg-[#FF6B00] hover:bg-[#e65a00] text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors" data-testid="design-upload-btn">
          {busy ? "Uploading…" : "Upload File"}
          <input
            type="file"
            className="hidden"
            accept="image/*,.pdf,.ai,.cdr,.eps,.psd,.svg,.tiff,.tif"
            onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
            disabled={busy}
          />
        </label>
      </div>
      {files.length > 0 && (
        <div className="mt-4 pt-4 border-t border-zinc-200 space-y-2" data-testid="design-files-list">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-3 text-sm p-2 rounded-lg bg-zinc-50">
              <div className="text-emerald-600">✓</div>
              <div className="flex-1">
                <div className="font-semibold truncate">{f.filename}</div>
                <div className="text-[10px] text-zinc-500">
                  {(f.size / 1024).toFixed(1)} KB · uploaded {fmtDateTime(f.uploaded_at)}
                </div>
              </div>
              <a href={f.url.startsWith("http") ? f.url : `${process.env.REACT_APP_BACKEND_URL}${f.url}`} target="_blank" rel="noreferrer" className="text-xs font-bold text-[#FF6B00] hover:underline">View</a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
